import React, { useState } from "react";
import axios from "axios";
import { Shield, EyeOff, Eye, Mail, Check, AlertTriangle, Copy, Download } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "../ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [showPasswordChangeDialog, setShowPasswordChangeDialog] = useState(false);
  const [passwordChangeData, setPasswordChangeData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [tempToken, setTempToken] = useState(null);
  const [lockedOutEmail, setLockedOutEmail] = useState(null);
  
  // 2FA States
  const [show2FAVerify, setShow2FAVerify] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFACode, setTwoFACode] = useState('');
  const [twoFAMethod, setTwoFAMethod] = useState('authenticator');
  const [setupData, setSetupData] = useState(null);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
  const [setupStep, setSetupStep] = useState(1); // 1: QR code, 2: verify, 3: recovery codes
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [pendingUserId, setPendingUserId] = useState(null);
  const [pendingUser, setPendingUser] = useState(null);
  
  const { loginWithToken } = useAuth();

  // Check for session expiration redirect
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('reason') === 'session_expired') {
      toast.info("Your session has expired. Please log in again.");
      // Clean up URL
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    void 0 && console.log('[LOGIN] Attempting login for:', formData.email);
    void 0 && console.log('[LOGIN] Current sessionStorage state:', {
      hasToken: !!sessionStorage.getItem('token'),
      hasLastActivity: !!sessionStorage.getItem('lastActivityTimestamp'),
      hasImpersonation: !!sessionStorage.getItem('impersonatedUserId')
    });
    
    // Clear any stale session data before login attempt
    sessionStorage.removeItem('impersonatedUserId');
    delete axios.defaults.headers.common['X-Impersonate-User'];
    
    try {
      // Feb 27, 2026 — Trim whitespace before sending.  Password
      // managers and email autocomplete frequently inject a stray
      // space which used to land in the regex-anchored email lookup
      // on the backend → "user not found" → "Invalid credentials".
      // Backend now strips too (defence in depth) but trimming here
      // saves a round-trip.
      const trimmedEmail = (formData.email || '').trim();
      const trimmedPassword = (formData.password || '').trim();
      const response = await axios.post(`${API}/auth/login`, {
        email: trimmedEmail,
        password: trimmedPassword
      });
      
      void 0 && console.log('[LOGIN] Login response received:', {
        hasAccessToken: !!response.data.access_token,
        hasUser: !!response.data.user,
        requires2FA: !!response.data.requires_2fa,
        requires2FASetup: !!response.data.requires_2fa_setup,
        requiresPasswordChange: !!response.data.requires_password_change
      });
      
      // Check if password change is required
      if (response.data.requires_password_change) {
        setTempToken(response.data.temp_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.temp_token}`;
        setPasswordChangeData({ ...passwordChangeData, oldPassword: formData.password });
        setShowPasswordChangeDialog(true);
        toast.info("You must change your temporary password");
        setLoading(false);
        return;
      }
      
      // Check if 2FA setup is required
      if (response.data.requires_2fa_setup) {
        setTempToken(response.data.temp_token);
        setPendingUserId(response.data.user_id);
        setPendingUser(response.data.user);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.temp_token}`;
        // Start 2FA setup
        await start2FASetup(response.data.temp_token);
        setLoading(false);
        return;
      }
      
      // Check if 2FA verification is required
      if (response.data.requires_2fa) {
        setTempToken(response.data.temp_token);
        setPendingUserId(response.data.user_id);
        setShow2FAVerify(true);
        setLoading(false);
        return;
      }
      
      // Normal login - use loginWithToken for consistent session setup
      const { access_token } = response.data;
      
      void 0 && console.log('[LOGIN] Using loginWithToken to verify and set session');
      const success = await loginWithToken(access_token);
      
      if (success) {
        toast.success("Login successful!");
      } else {
        toast.error("Login verification failed. Please try again.");
      }
    } catch (error) {
      console.error('[LOGIN] Login failed:', error.message, error.response?.data);
      const errorDetail = error.response?.data?.detail || "Invalid credentials. Contact your administrator for access.";
      
      // Check if account is locked
      if (errorDetail.includes("Account locked")) {
        setLockedOutEmail(formData.email);
        toast.error(errorDetail);
      } else {
        toast.error(errorDetail);
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Start 2FA setup - fetch QR code and recovery codes
  const start2FASetup = async (token) => {
    try {
      const response = await axios.post(`${API}/auth/setup-2fa`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSetupData(response.data);
      setRecoveryCodes(response.data.recovery_codes || []);
      setSetupStep(1);
      setShow2FASetup(true);
    } catch (error) {
      toast.error("Failed to start 2FA setup");
    }
  };
  
  // Verify 2FA setup with code from authenticator
  const verify2FASetup = async () => {
    if (!twoFACode || twoFACode.length !== 6) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('code', twoFACode);
      
      await axios.post(`${API}/auth/verify-2fa-setup`, formData, {
        headers: { 'Authorization': `Bearer ${tempToken}` }
      });
      
      toast.success("2FA enabled successfully!");
      setSetupStep(3); // Show recovery codes
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code");
    } finally {
      setLoading(false);
    }
  };
  
  // Complete 2FA setup and login
  const complete2FASetup = async () => {
    // Backend clears requires_2fa_setup during verify-2fa-setup.
    // Re-login to get proper token
    setShow2FASetup(false);
    setSetupData(null);
    setTwoFACode('');
    setSetupStep(1);
    toast.info("Please log in again with 2FA");
  };
  
  // Verify 2FA code during login
  const verify2FALogin = async () => {
    if (!twoFACode) {
      toast.error("Please enter a verification code");
      return;
    }
    
    setLoading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('code', twoFACode);
      formDataObj.append('temp_token', tempToken);
      formDataObj.append('method', twoFAMethod);
      
      const response = await axios.post(`${API}/auth/verify-2fa`, formDataObj);
      
      // Use loginWithToken to verify and set up the session
      const success = await loginWithToken(response.data.access_token);
      
      if (success) {
        toast.success("Login successful!");
      } else {
        toast.error("Login verification failed. Please try again.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid verification code");
    } finally {
      setLoading(false);
    }
  };
  
  // Request email OTP
  const requestEmailOtp = async () => {
    setLoading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append('temp_token', tempToken);
      
      const response = await axios.post(`${API}/auth/send-email-otp`, formDataObj);
      toast.success(response.data.message);
      setEmailOtpSent(true);
      setTwoFAMethod('email');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email code");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotPasswordEmail) {
      toast.error("Please enter your email address");
      return;
    }
    
    try {
      setLoading(true);
      await axios.post(`${API}/auth/request-password-reset`, { 
        email: forgotPasswordEmail 
      });
      toast.success("Password reset request submitted. An administrator will review your request shortly.");
      setShowForgotPassword(false);
      setForgotPasswordEmail('');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit password reset request");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordChangeData.newPassword !== passwordChangeData.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (passwordChangeData.newPassword.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }
    
    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('old_password', passwordChangeData.oldPassword);
      formData.append('new_password', passwordChangeData.newPassword);
      
      await axios.post(`${API}/auth/change-password`, formData, {
        headers: {
          'Authorization': `Bearer ${tempToken}`
        }
      });
      
      toast.success("Password changed successfully! Please log in with your new password.");
      setShowPasswordChangeDialog(false);
      setPasswordChangeData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setTempToken(null);
      delete axios.defaults.headers.common['Authorization'];
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-emerald-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl font-bold text-gray-800 mb-2">
              Secure Access
            </CardTitle>
            <div className="flex items-center justify-center mb-4">
              <Shield className="h-8 w-8 text-teal-600 mr-2" />
              <span className="text-lg font-semibold text-teal-700">CareVaultPro</span>
            </div>
            <p className="text-sm text-gray-600">HIPAA Compliant Medical Records</p>
            <p className="text-xs text-gray-500 mt-1">Authorized Personnel Only</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="auth-email"
                  placeholder="your.email@organization.com"
                />
              </div>
              
              <div>
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    data-testid="auth-password"
                    placeholder="Enter your password"
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-teal-600 hover:bg-teal-700"
                disabled={loading}
                data-testid="auth-submit"
              >
                {loading ? 'Authenticating...' : 'Sign In'}
              </Button>
            </form>
            
            <div className="mt-4 text-center space-y-2">
              <button
                type="button"
                onClick={() => {
                  setForgotPasswordEmail(formData.email);
                  setShowForgotPassword(true);
                }}
                className="text-sm text-teal-600 hover:text-teal-700 hover:underline"
              >
                Forgot Password?
              </button>
              
              {lockedOutEmail && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                  <p className="text-sm text-red-800 mb-2">Account locked due to multiple failed attempts.</p>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setForgotPasswordEmail(lockedOutEmail);
                      setShowForgotPassword(true);
                      setLockedOutEmail(null);
                    }}
                    className="w-full"
                  >
                    Request Password Reset
                  </Button>
                </div>
              )}
              
              <p className="text-xs text-gray-500 mt-3">
                Need access? Contact your system administrator
              </p>
            </div>
          </CardContent>
        </Card>
        
        {/* Forgot Password Dialog */}
        <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Request Password Reset</DialogTitle>
              <p className="text-sm text-gray-600">
                Enter your email address and an administrator will review your request.
              </p>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="forgot-email">Email Address</Label>
                <Input
                  id="forgot-email"
                  name="forgot-email"
                  type="email"
                  autoComplete="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  placeholder="your.email@organization.com"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <Button variant="outline" onClick={() => setShowForgotPassword(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleForgotPassword}
                disabled={loading}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {loading ? 'Submitting...' : 'Submit Request'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Force Password Change Dialog */}
        <Dialog open={showPasswordChangeDialog} onOpenChange={() => {}}>
          <DialogContent className="max-w-md" onInteractOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle>Change Your Password</DialogTitle>
              <p className="text-sm text-gray-600">
                You are using a temporary password. Please set a new permanent password to continue.
              </p>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="new-password">New Password</Label>
                <Input
                  id="new-password"
                  name="new-password"
                  type="password"
                  autoComplete="new-password"
                  value={passwordChangeData.newPassword}
                  onChange={(e) => setPasswordChangeData({...passwordChangeData, newPassword: e.target.value})}
                  placeholder="Enter new password"
                />
                <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  value={passwordChangeData.confirmPassword}
                  onChange={(e) => setPasswordChangeData({...passwordChangeData, confirmPassword: e.target.value})}
                  placeholder="Confirm new password"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <Button 
                onClick={handlePasswordChange}
                disabled={loading || !passwordChangeData.newPassword || !passwordChangeData.confirmPassword}
                className="bg-teal-600 hover:bg-teal-700 w-full"
              >
                {loading ? 'Changing Password...' : 'Change Password'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* 2FA Verification Dialog (during login) */}
        <Dialog open={show2FAVerify} onOpenChange={setShow2FAVerify}>
          <DialogContent className="max-w-md" onInteractOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-teal-600" />
                Two-Factor Authentication
              </DialogTitle>
              <p className="text-sm text-gray-600">
                Enter the verification code to complete your login.
              </p>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Method Selection */}
              <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
                <button
                  type="button"
                  onClick={() => { setTwoFAMethod('authenticator'); setTwoFACode(''); }}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    twoFAMethod === 'authenticator' ? 'bg-white shadow text-teal-700' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Authenticator App
                </button>
                <button
                  type="button"
                  onClick={() => { setTwoFAMethod('email'); setTwoFACode(''); }}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    twoFAMethod === 'email' ? 'bg-white shadow text-teal-700' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Email Code
                </button>
                <button
                  type="button"
                  onClick={() => { setTwoFAMethod('recovery'); setTwoFACode(''); }}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    twoFAMethod === 'recovery' ? 'bg-white shadow text-teal-700' : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Recovery Code
                </button>
              </div>
              
              {/* Authenticator Method */}
              {twoFAMethod === 'authenticator' && (
                <div>
                  <Label htmlFor="2fa-code">Enter 6-digit code from your authenticator app</Label>
                  <Input
                    id="2fa-code"
                    name="2fa-code"
                    type="text"
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-2xl tracking-widest mt-2"
                    maxLength={6}
                    autoComplete="one-time-code"
                  />
                </div>
              )}
              
              {/* Email OTP Method */}
              {twoFAMethod === 'email' && (
                <div>
                  {!emailOtpSent ? (
                    <div className="text-center py-4">
                      <Mail className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                      <p className="text-sm text-gray-600 mb-4">We'll send a verification code to your email address.</p>
                      <Button 
                        onClick={requestEmailOtp}
                        disabled={loading}
                        className="bg-teal-600 hover:bg-teal-700"
                      >
                        {loading ? 'Sending...' : 'Send Email Code'}
                      </Button>
                    </div>
                  ) : (
                    <div>
                      <Label htmlFor="email-code">Enter 6-digit code from your email</Label>
                      <Input
                        id="email-code"
                        name="email-code"
                        type="text"
                        autoComplete="one-time-code"
                        value={twoFACode}
                        onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000"
                        className="text-center text-2xl tracking-widest mt-2"
                        maxLength={6}
                      />
                      <button
                        type="button"
                        onClick={requestEmailOtp}
                        disabled={loading}
                        className="text-sm text-teal-600 hover:underline mt-2"
                      >
                        Resend code
                      </button>
                    </div>
                  )}
                </div>
              )}
              
              {/* Recovery Code Method */}
              {twoFAMethod === 'recovery' && (
                <div>
                  <Label htmlFor="recovery-code">Enter one of your recovery codes</Label>
                  <Input
                    id="recovery-code"
                    name="recovery-code"
                    type="text"
                    autoComplete="off"
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.toUpperCase())}
                    placeholder="XXXX-XXXX"
                    className="text-center text-xl tracking-wider mt-2 uppercase"
                    maxLength={9}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Recovery codes can only be used once. After using this code, it will be invalidated.
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-2 mt-6">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShow2FAVerify(false);
                  setTwoFACode('');
                  setTwoFAMethod('authenticator');
                  setEmailOtpSent(false);
                }}
              >
                Cancel
              </Button>
              <Button 
                onClick={verify2FALogin}
                disabled={loading || !twoFACode || (twoFAMethod === 'email' && !emailOtpSent)}
                className="bg-teal-600 hover:bg-teal-700"
              >
                {loading ? 'Verifying...' : 'Verify & Login'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* 2FA Setup Dialog (required setup) */}
        <Dialog open={show2FASetup} onOpenChange={() => {}}>
          <DialogContent className="max-w-lg" onInteractOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-teal-600" />
                Set Up Two-Factor Authentication
              </DialogTitle>
              <p className="text-sm text-gray-600">
                Your organization requires two-factor authentication. Follow these steps to secure your account.
              </p>
            </DialogHeader>
            
            {/* Step Indicator */}
            <div className="flex items-center justify-center gap-2 my-4">
              {[1, 2, 3].map((step) => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${
                    setupStep === step ? 'bg-teal-600 text-white' : 
                    setupStep > step ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {setupStep > step ? <Check className="h-4 w-4" /> : step}
                  </div>
                  {step < 3 && <div className={`w-8 h-1 ${setupStep > step ? 'bg-green-500' : 'bg-gray-200'}`} />}
                </div>
              ))}
            </div>
            
            {/* Step 1: QR Code */}
            {setupStep === 1 && setupData && (
              <div className="space-y-4">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-4">
                    Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
                  </p>
                  <div className="flex justify-center">
                    <img src={setupData.qr_code} alt="2FA QR Code" className="w-48 h-48 border rounded" />
                  </div>
                  <p className="text-xs text-gray-500 mt-4">
                    Can't scan? Enter this code manually: <code className="bg-gray-100 px-2 py-1 rounded text-xs">{setupData.secret}</code>
                  </p>
                </div>
                <div className="flex justify-end">
                  <Button onClick={() => setSetupStep(2)} className="bg-teal-600 hover:bg-teal-700">
                    Next: Verify Code
                  </Button>
                </div>
              </div>
            )}
            
            {/* Step 2: Verify Code */}
            {setupStep === 2 && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="setup-code">Enter the 6-digit code from your authenticator app</Label>
                  <Input
                    id="setup-code"
                    type="text"
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="text-center text-2xl tracking-widest mt-2"
                    maxLength={6}
                    autoComplete="one-time-code"
                  />
                </div>
                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setSetupStep(1)}>
                    Back
                  </Button>
                  <Button 
                    onClick={verify2FASetup}
                    disabled={loading || !twoFACode || twoFACode.length !== 6}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    {loading ? 'Verifying...' : 'Verify Code'}
                  </Button>
                </div>
              </div>
            )}
            
            {/* Step 3: Recovery Codes */}
            {setupStep === 3 && (
              <div className="space-y-4">
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-yellow-800">Save Your Recovery Codes</p>
                      <p className="text-xs text-yellow-700 mt-1">
                        These codes can be used to access your account if you lose your authenticator. 
                        Each code can only be used once. Store them securely.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-2 bg-gray-50 p-4 rounded-lg font-mono text-sm">
                  {recoveryCodes.map((code, index) => (
                    <div key={index} className="bg-white px-3 py-2 rounded border text-center">
                      {code}
                    </div>
                  ))}
                </div>
                
                <div className="flex gap-2">
                  <Button 
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(recoveryCodes.join('\n'));
                      toast.success('Recovery codes copied to clipboard');
                    }}
                    className="flex-1"
                  >
                    <Copy className="h-4 w-4 mr-2" /> Copy Codes
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => {
                      const blob = new Blob([`CareVaultPro EMR Recovery Codes\n${'='.repeat(30)}\n\n${recoveryCodes.join('\n')}\n\nKeep these codes secure. Each can only be used once.`], {type: 'text/plain'});
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'carevaultpro-recovery-codes.txt';
                      a.click();
                    }}
                    className="flex-1"
                  >
                    <Download className="h-4 w-4 mr-2" /> Download
                  </Button>
                </div>
                
                <div className="flex items-center gap-2 mt-4">
                  <input 
                    type="checkbox" 
                    id="confirm-saved" 
                    onChange={(e) => setShowRecoveryCodes(e.target.checked)}
                  />
                  <Label htmlFor="confirm-saved" className="text-sm">
                    I have saved my recovery codes in a secure location
                  </Label>
                </div>
                
                <div className="flex justify-end">
                  <Button 
                    onClick={complete2FASetup}
                    disabled={!showRecoveryCodes}
                    className="bg-teal-600 hover:bg-teal-700"
                  >
                    Complete Setup
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Login;
